import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import { getCachedShippingCost, setCachedShippingCost, hasValidCache } from '../../utils/shippingCostCache';

// Stripe publishable key is fetched at runtime from the business integrations API.
// Falls back to REACT_APP_STRIPE_PUBLISHABLE_KEY env var if the API is unavailable.
const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

async function fetchStripePublishableKey() {
  try {
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const url = `${backendUrl}/api/v1/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key from integrations API, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

export const AFTERPAY_SESSION_STORAGE_KEY = 'afterpayCheckoutSession';

/** SessionStorage key prefix for itemized receipt on success page (`${prefix}${orderNumber}`). */
export const ORDER_RECEIPT_STORAGE_PREFIX = 'rs_order_receipt_v1:';

/** donation | event | product | mixed — used for post-checkout thank-you line */
function computeOrderKind(cartItems, isDonationOnly) {
  if (isDonationOnly) return 'donation';
  if (!Array.isArray(cartItems) || !cartItems.length) return 'product';
  const isEvent = (i) => i.type === 'event' || i.eventId;
  const isDon = (i) => i.type === 'donation' || i.productType === 'donation';
  const hasE = cartItems.some(isEvent);
  const hasD = cartItems.some(isDon);
  const hasP = cartItems.some((i) => !isEvent(i) && !isDon(i));
  if ((hasE && hasP) || (hasE && hasD) || (hasD && hasP)) return 'mixed';
  if (hasE) return 'event';
  if (hasD) return 'donation';
  return 'product';
}

function lineSummaryFromCart(cartItems) {
  if (!Array.isArray(cartItems)) return '';
  return cartItems
    .map((i) => i.name || i.title || i.productName)
    .filter(Boolean)
    .join(' · ');
}

const CheckoutForm = ({ 
  clientSecret, 
  processing, 
  setProcessing, 
  error, 
  setError, 
  orderCompleted, 
  setOrderCompleted,
  /** Fires the exact subtotal−discount+shipping total (cents) used for the Place Order button — parent PaymentIntent must match to avoid overcharging. */
  onPayableAmountCentsChange
}) => {
  console.log('🔍 CheckoutForm: Component rendered with clientSecret:', clientSecret);
  console.log('🔍 CheckoutForm: Component props:', { clientSecret });
  
  // Dev/staging only: set REACT_APP_CHECKOUT_BYPASS=true to skip Stripe (never enable in production builds).
  const BYPASS_PAYMENT = process.env.REACT_APP_CHECKOUT_BYPASS === 'true';

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  
  console.log('🔍 CheckoutForm: Stripe instance:', stripe);
  console.log('🔍 CheckoutForm: Elements instance:', elements);
  console.log('🔍 CheckoutForm: Client Secret:', clientSecret);
  console.log('🔍 CheckoutForm: Stripe instance type:', typeof stripe);
  console.log('🔍 CheckoutForm: Elements instance type:', typeof elements);
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const { customer, isAuthenticated: isShopCustomer, getAuthHeaders } = useCustomerAuth();
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    apartment: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Australia'
  });

  const [paymentMethod, setPaymentMethod] = useState('card');

  // Donation-only cart: hide irrelevant fields
  const isDonationOnly = Array.isArray(cartItems) && cartItems.length > 0 &&
    cartItems.every(item => item.type === 'donation' || item.productType === 'donation');

  // Save-address preference
  const [saveAddress, setSaveAddress] = useState(true);

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null); // { discountAmount, message }
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);

  // Order notes
  const [orderNotes, setOrderNotes] = useState('');

  // Normalize Australian state abbreviations to full names
  const normalizeState = (state) => {
    if (!state) return '';
    
    const stateMap = {
      'NSW': 'New South Wales',
      'VIC': 'Victoria',
      'QLD': 'Queensland',
      'SA': 'South Australia',
      'WA': 'Western Australia',
      'TAS': 'Tasmania',
      'NT': 'Northern Territory',
      'ACT': 'Australian Capital Territory',
      // Also handle common variations
      'New South Wale': 'New South Wales', // Fix typo if present
    };
    
    // Check if it's an abbreviation
    const normalized = stateMap[state.toUpperCase()] || stateMap[state];
    
    // If not found in map, return original (might already be full name)
    return normalized || state;
  };

  // Apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    setDiscountError('');
    setDiscountApplied(null);
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/orders/validate-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode.trim().toUpperCase(), subtotal }),
      });
      const data = await res.json();
      if (data.valid) {
        setDiscountApplied(data);
      } else {
        setDiscountError(data.message || 'Invalid code');
      }
    } catch {
      setDiscountError('Could not validate code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      console.log('🔍 Auto-filling checkout with user data:', user);
      setCustomerInfo({
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        phone: user.phone || ''
      });
      
      if (user.address) {
        // Normalize state value to match dropdown options
        const normalizedState = normalizeState(user.address.state);
        
        setShippingAddress({
          street: user.address.street || '',
          apartment: '',
          city: user.address.city || '',
          state: normalizedState,
          postalCode: user.address.postcode || '',
          country: user.address.country || 'Australia'
        });
      }
    }
  }, [user]);

  // Rainbow Sails shop customer account (JWT) — prefill + order will link to CRM Customer on backend.
  useEffect(() => {
    if (!isShopCustomer || !customer) return;
    setCustomerInfo((prev) => ({
      name:
        prev.name ||
        `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
        customer.email ||
        '',
      email: prev.email || customer.email || '',
      phone: prev.phone || customer.phone || '',
    }));
  }, [isShopCustomer, customer]);
  
  // const [stripeError, setStripeError] = useState(null); // Unused for now
  
  // Shipping cost state (fetched from API - uses monthly stored Printful max shipping cost)
  // Initialize with cached value (with expiration check), default to 0 if no cache
  const [shippingCost, setShippingCost] = useState(getCachedShippingCost() || 0);
  const [shippingCostLoading, setShippingCostLoading] = useState(true);
  
  // Fetch shipping cost from API only if cache is missing or expired
  // The backend updates shipping cost monthly via cron job
  useEffect(() => {
    const fetchShippingCost = async () => {
      try {
        // Only physical / print-on-demand / bundle / rental items need shipping
        const PHYSICAL_TYPES = ['physical', 'print_on_demand', 'bundle', 'rental'];
        const hasPhysical = (cartItems || []).some(
          item => PHYSICAL_TYPES.includes(item.type) || PHYSICAL_TYPES.includes(item.productType)
        );
        if (!hasPhysical) {
          setShippingCost(0);
          setShippingCostLoading(false);
          return;
        }

        const subtotal = getTotalPrice();
        if (subtotal > 100) {
          setShippingCost(0); // Matches backend order shipping: free when subtotal > 100 AUD
          setShippingCostLoading(false);
          return;
        }
        
        // Check if we have a valid cache first (saves API calls)
        if (hasValidCache()) {
          const cached = getCachedShippingCost();
          if (cached) {
            setShippingCost(cached);
            setShippingCostLoading(false);
            return; // Use cached value, no API call needed
          }
        }
        
        // Only fetch if cache is missing or expired
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const apiBase = process.env.REACT_APP_API_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
        const response = await fetch(`${apiBase}/orders/get-shipping-cost`);
        if (response.ok) {
          const data = await response.json();
          if (data.shippingCost) {
            setShippingCost(data.shippingCost);
            // Cache the value for 30 days
            setCachedShippingCost(data.shippingCost);
          } else {
            // If no value from API, use cached fallback (even if expired)
            const cached = getCachedShippingCost();
            if (cached) setShippingCost(cached);
          }
        } else {
          console.warn('⚠️ Failed to fetch shipping cost, using cached value');
          const cached = getCachedShippingCost();
          if (cached) setShippingCost(cached);
        }
      } catch (error) {
        console.error('❌ Error fetching shipping cost:', error);
        // Use cached value as fallback (even if expired)
        const cached = getCachedShippingCost();
        if (cached) setShippingCost(cached);
      } finally {
        setShippingCostLoading(false);
      }
    };
    
    fetchShippingCost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, getTotalPrice]); // Update when cart items change - shippingCost intentionally excluded to avoid infinite loop
  
  // Calculate costs — prices are GST-inclusive; `tax` is the GST *portion* for display only
  const subtotal = getTotalPrice();
  const discountAmount = discountApplied ? Math.min(discountApplied.discountAmount, subtotal) : 0;
  const nonDonationSubtotal = (cartItems || [])
    .filter(item => (item.type !== 'donation' && item.productType !== 'donation'))
    .reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  let tax = 0;
  if (!isDonationOnly && subtotal > 0) {
    const discountOnNonDonation = discountAmount * (nonDonationSubtotal / subtotal);
    const netNonDonation = Math.max(0, nonDonationSubtotal - discountOnNonDonation);
    tax = netNonDonation * (0.1 / 1.1);
  }
  const total = Math.max(0, subtotal - discountAmount) + (shippingCost || 0);

  // Single source of truth for Stripe: same dollars as the button (includes discount + shipping the form uses)
  useLayoutEffect(() => {
    if (typeof onPayableAmountCentsChange === 'function') {
      onPayableAmountCentsChange(Math.max(0, Math.round(total * 100)));
    }
  }, [total, onPayableAmountCentsChange]);
  
  // Track checkout start
  useEffect(() => {
    analyticsService.trackCheckoutStart(cartItems, total);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleSubmit = async (e) => {
    console.log('🔵 FORM SUBMITTED - handleSubmit called');
    e.preventDefault();

    console.log('🔵 Stripe available:', !!stripe);
    console.log('🔵 Elements available:', !!elements);
    console.log('🔵 Customer info:', customerInfo);
    console.log('🔵 Shipping address:', shippingAddress);
    console.log('🔵 Payment method:', paymentMethod);

    // Only require Stripe/Elements if not in bypass mode
    if (paymentMethod === 'card' && !BYPASS_PAYMENT && (!stripe || !elements)) {
      console.log('❌ Stripe or Elements not available for card payment, returning');
      return;
    }

    // Validation
    if (!customerInfo.name || !customerInfo.email) {
      console.log('❌ Validation failed: missing customer info');
      setError('Please fill in customer information');
      return;
    }

    if (!isDonationOnly && (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postalCode)) {
      console.log('❌ Validation failed: missing shipping address fields');
      console.log('🔍 Shipping address state:', shippingAddress);
      setError('Please fill in all required shipping address fields (street, city, state, postal code)');
      return;
    }

    console.log('✅ All validation passed, starting payment processing...');
    setProcessing(true);
    setError(null);

    logger.log('info', 'checkout', 'Processing payment', {
      total: total,
      itemCount: cartItems.length,
      paymentMethod
    });

    const baseOrderData = {
      customer: {
        name: customerInfo.name,
        email: customerInfo.email,
        phone: customerInfo.phone
      },
      shippingAddress: isDonationOnly ? undefined : {
        line1: shippingAddress.street,
        line2: shippingAddress.apartment || undefined,
        city: shippingAddress.city,
        state: shippingAddress.state,
        postalCode: shippingAddress.postalCode,
        country: shippingAddress.country
      },
      items: cartItems.map(item => ({
        productId: item.productId || item.id,
        productName: item.title || item.name,
        variantId: item.variantId,
        variantName: item.variantName || item.title,
        printfulSyncVariantId: item.printfulVariantId,
        printfulVariantId: item.printfulVariantId,
        printfulProductId: item.printfulProductId,
        quantity: item.quantity,
        price: item.price,
        imageUrl: item.image || item.imageUrl || item.customImage || item.previewImage
      })),
      subtotal: subtotal,
      shippingCost: shippingCost,
      tax: tax,
      total: total,
      currency: 'AUD'
    };

    if (paymentMethod === 'afterpay') {
      try {
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
        const merchantReference = `ZEE-AFTERPAY-${Date.now()}`;

        const response = await fetch(`${backendUrl}/api/orders/afterpay/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            order: {
              ...baseOrderData,
              paymentMethod: 'Afterpay',
              merchantReference
            },
            redirectUrls: {
              confirmUrl: `${window.location.origin}/checkout/afterpay-return?status=approved`,
              cancelUrl: `${window.location.origin}/checkout/afterpay-return?status=cancelled`
            },
            merchantReference
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const message = data?.message || data?.error || `Afterpay checkout failed (${response.status})`;
          throw new Error(message);
        }

        analyticsService.trackCheckoutStep('afterpay_redirect', {
          token: data.token,
          merchantReference: data.merchantReference
        });

        sessionStorage.setItem(
          AFTERPAY_SESSION_STORAGE_KEY,
          JSON.stringify({
            token: data.token,
            merchantReference: data.merchantReference,
            checkoutUrl: data.checkoutUrl,
            order: {
              ...baseOrderData,
              paymentMethod: 'Afterpay',
              merchantReference
            },
            createdAt: Date.now()
          })
        );

        window.location.href = data.checkoutUrl;
        return;
      } catch (afterpayError) {
        console.error('❌ Afterpay checkout error:', afterpayError);
        setError(afterpayError.message || 'Failed to start Afterpay checkout');
        setProcessing(false);
        logger.log('error', 'checkout', 'Afterpay checkout failed', {
          error: afterpayError.message
        });
        return;
      }
    }

    try {
      let paymentIntent = null;

      if (BYPASS_PAYMENT) {
        console.log('⚠️ BYPASS MODE: Skipping Stripe payment, creating test order');
        paymentIntent = {
          id: 'test_payment_' + Date.now(),
          status: 'succeeded'
        };
      } else {
        console.log('🔍 Starting Stripe payment confirmation...');
        console.log('🔍 Stripe instance:', !!stripe);
        console.log('🔍 Elements instance:', !!elements);

        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/checkout/success`,
          },
          redirect: 'if_required'
        });

        console.log('🔍 Stripe payment result:', result);

        const { error: stripeError, paymentIntent: intent } = result;

        if (stripeError) {
          console.error('❌ Stripe error:', stripeError);
          setError(stripeError.message);
          setProcessing(false);
          logger.log('error', 'checkout', 'Payment failed', { 
            error: stripeError.message,
            type: stripeError.type,
            code: stripeError.code
          });
          return;
        }

        paymentIntent = intent;

        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
          console.error('❌ Payment not successful. Status:', paymentIntent?.status);
          setError('Payment was not successful. Please try again.');
          setProcessing(false);
          logger.log('error', 'checkout', 'Payment not succeeded', { 
            status: paymentIntent?.status
          });
          return;
        }
      }

      logger.log('info', 'checkout', 'Payment successful', {
        paymentIntentId: paymentIntent.id,
        status: paymentIntent.status
      });

      analyticsService.trackCheckoutStep('payment_completed', {
        paymentIntentId: paymentIntent.id
      });

      // Format order data to match backend schema
      const orderData = {
        items: cartItems.map((item) => {
          if (item.type === 'event' || item.eventId) {
            return {
              eventId: item.eventId || String(item.id).replace(/^event-/, ''),
              quantity: item.quantity,
            };
          }
          return {
            productId: item.productId || item.id,
            variantId: item.variantId,
            quantity: item.quantity,
            price: item.price,
          };
        }),
        shippingAddress: isDonationOnly ? undefined : {
          line1: shippingAddress.street,
          line2: shippingAddress.apartment || undefined,
          city: shippingAddress.city,
          state: shippingAddress.state,
          postalCode: shippingAddress.postalCode,
          country: shippingAddress.country
        },
        discountCode: discountApplied ? discountCode.trim().toUpperCase() : undefined,
        /** Client already paid — link order to this PI and skip duplicate charge on server */
        stripePaymentIntentId:
          paymentIntent?.id && String(paymentIntent.id).startsWith('pi_') ? paymentIntent.id : undefined,
        customerEmail: (customerInfo.email || '').trim() || undefined,
        customerName: (customerInfo.name || '').trim() || undefined,
        customerNotes: [
          `Customer: ${customerInfo.name} (${customerInfo.email})${customerInfo.phone ? ` - Phone: ${customerInfo.phone}` : ''}`,
          orderNotes ? `Notes: ${orderNotes}` : null,
        ].filter(Boolean).join(' | '),
      };

      logger.log('info', 'checkout', 'Creating order', orderData);

      // Use the same API config as the rest of the frontend
      // Use the same API config as the rest of the frontend
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('rainbow-sails.shop') ||
         (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 
        (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
      // Use v1 endpoint - it handles orders with or without payment
      const response = await fetch(`${backendUrl}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      console.log('🔍 Backend response:', result);

      // Backend returns { order, ... } or { error, ... }
      if (!response.ok || result.error) {
        throw new Error(result.message || result.error || 'Failed to create order');
      }

      const orderNumber = result.order?.orderNumber || result.orderNumber;
      const printfulId = result.printfulOrderId || result.order?.printfulOrderId || '';
      logger.log('info', 'checkout', 'Order created', {
        orderNumber: orderNumber,
        printfulOrderId: printfulId
      });

      try {
        const o = result.order;
        if (o && orderNumber) {
          const shopBusinessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();
          const orderKind = computeOrderKind(cartItems, isDonationOnly);
          const firstName = (customerInfo.name || '').trim().split(/\s+/)[0] || '';
          const lineSummary = lineSummaryFromCart(cartItems) ||
            (o.items || []).map((it) => it.name).filter(Boolean).join(' · ') ||
            '';
          const receipt = {
            orderNumber,
            isDonationOnly,
            businessId: shopBusinessId,
            firstName,
            orderKind,
            lineSummary,
            printfulOrderId: printfulId || o.printfulOrderId || '',
            subtotal: o.subtotal,
            tax: o.tax,
            shipping: o.shipping,
            discount: o.discount,
            total: o.total,
            items: (o.items || []).map((it) => ({
              name: it.name,
              quantity: it.quantity,
              price: it.price,
              total: it.total,
            })),
            createdAt: o.createdAt,
          };
          sessionStorage.setItem(ORDER_RECEIPT_STORAGE_PREFIX + orderNumber, JSON.stringify(receipt));
        }
      } catch (receiptErr) {
        console.warn('[checkout] Could not cache receipt for success page', receiptErr);
      }

      // Save address to customer profile if logged in and opted in
      if (isShopCustomer && saveAddress && !isDonationOnly) {
        try {
          const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1').replace(/\/$/, '');
          fetch(`${apiBase.replace('/api/v1', '')}/api/v1/customer-auth/me`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              phone: customerInfo.phone || undefined,
              shippingAddress: {
                address1: shippingAddress.street,
                address2: shippingAddress.apartment || undefined,
                city: shippingAddress.city,
                state: shippingAddress.state,
                postcode: shippingAddress.postalCode,
                country: shippingAddress.country,
              },
            }),
          });
        } catch { /* non-critical, don't block navigation */ }
      }

      setOrderCompleted(true);
      clearCart();
      {
        const bid = (process.env.REACT_APP_BUSINESS_ID || '').trim();
        const fn = (customerInfo.name || '').trim().split(/\s+/)[0] || '';
        const q = new URLSearchParams();
        q.set('order', orderNumber);
        q.set('printful', printfulId || '');
        if (bid) q.set('bid', bid);
        if (fn) q.set('fn', fn);
        navigate(`/checkout/success?${q.toString()}`);
      }

    } catch (err) {
      console.error('Order creation error:', err);
      setError(err.message || 'Failed to create order');
      setProcessing(false);

      logger.log('error', 'checkout', 'Order creation failed', {
        error: err.message
      });
    }
  };
  
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
      minHeight: '100vh',
      padding: isMobile ? '1rem 0.5rem' : '2rem 1rem',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      <form onSubmit={handleSubmit} style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: isMobile ? '1.5rem' : '3rem',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Rainbow Sails Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '4rem',
            opacity: 0.1,
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}>
            🌈
          </div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            textShadow: '0 0 30px rgba(139, 92, 246, 0.5)'
          }}>
            Rainbow Sails
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: 0
          }}>
            Your Order Information
          </p>
        </div>
      
      {error && (
        <div style={{
          padding: '1rem',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '8px',
          color: '#fecaca',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}
      
      {/* Customer Information */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem', 
          background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          fontWeight: 'bold'
        }}>
          {user ? '✨ Your Information' : '👤 Customer Information'}
        </h2>
        {user && (
          <div style={{
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.2)',
            border: '1px solid rgba(34, 197, 94, 0.5)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            color: '#bbf7d0',
            fontSize: '0.9rem'
          }}>
            ✅ Logged in as {user.email} - Your details have been pre-filled
          </div>
        )}
        <div style={{ display: 'grid', gap: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
          <input
            type="text"
            placeholder="Full Name *"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            required
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '1.2rem',
              borderRadius: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1.1rem',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.8)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'}
          />
          <input
            type="email"
            placeholder="Email Address *"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            required
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1rem',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="tel"
            placeholder="Phone Number"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>
      
      {/* Shipping Address — hidden for donation-only carts */}
      {!isDonationOnly && <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'white' }}>
          Shipping Address
        </h2>
        <div style={{ display: 'grid', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
          <input
            type="text"
            placeholder="Street Address *"
            value={shippingAddress.street}
            onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
            required
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="text"
            placeholder="Apartment, Suite, etc."
            value={shippingAddress.apartment}
            onChange={(e) => setShippingAddress({ ...shippingAddress, apartment: e.target.value })}
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', 
            gap: '1rem',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <input
              type="text"
              placeholder="City *"
              value={shippingAddress.city}
              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
              required
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            <select
              name="state"
              value={shippingAddress.state}
              onChange={(e) => {
                console.log('🔍 State changed to:', e.target.value);
                setShippingAddress({ ...shippingAddress, state: e.target.value });
              }}
              required
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select an option...</option>
              <option value="Australian Capital Territory">Australian Capital Territory</option>
              <option value="New South Wales">New South Wales</option>
              <option value="Northern Territory">Northern Territory</option>
              <option value="Queensland">Queensland</option>
              <option value="South Australia">South Australia</option>
              <option value="Tasmania">Tasmania</option>
              <option value="Victoria">Victoria</option>
              <option value="Western Australia">Western Australia</option>
            </select>
            <input
              type="text"
              placeholder="Postal Code *"
              value={shippingAddress.postalCode}
              onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
              required
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <input
            type="text"
            value={shippingAddress.country}
            readOnly
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#9ca3af',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Save address to profile checkbox */}
        {isShopCustomer && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', cursor: 'pointer', color: '#d1d5db', fontSize: '14px' }}>
            <input type="checkbox" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#8b5cf6' }} />
            Save this address to my profile for future orders
          </label>
        )}
      </div>}

      {/* Order notes */}
      <div style={{ marginBottom: '2rem' }}>
        <textarea
          placeholder="Order notes (optional) — gift message, delivery instructions, etc."
          value={orderNotes}
          onChange={e => setOrderNotes(e.target.value)}
          rows={2}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)',
            color: 'white', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Order Summary */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'white' }}>
          Order Summary
        </h2>
        {BYPASS_PAYMENT && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              background: 'rgba(234, 179, 8, 0.2)',
              border: '1px solid rgba(250, 204, 21, 0.5)',
              color: '#fef9c3',
              fontSize: '0.9rem',
            }}
          >
            Test mode: payment bypass is on (<code style={{ color: '#fff' }}>REACT_APP_CHECKOUT_BYPASS=true</code>).
            Turn it off for live charges.
          </div>
        )}
        
        {cartItems.map((item) => (
          <div key={item.id} style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <span style={{ color: '#d1d5db' }}>
              {item.name} — {item.quantity} × ${Number(item.price || 0).toFixed(2)}
            </span>
            <span style={{ color: 'white', fontWeight: '600' }}>
              ${(Number(item.price || 0) * item.quantity).toFixed(2)}
            </span>
          </div>
        ))}
        
        {/* Discount code */}
        <div style={{ margin: '1rem 0', display: 'flex', gap: '8px' }}>
          <input
            type="text"
            placeholder="Discount / coupon code"
            value={discountCode}
            onChange={e => { setDiscountCode(e.target.value); setDiscountError(''); if (!e.target.value.trim()) setDiscountApplied(null); }}
            onKeyDown={e => e.key === 'Enter' && handleApplyDiscount()}
            style={{
              flex: 1, padding: '0.6rem 0.9rem', borderRadius: '8px',
              border: discountApplied ? '1px solid #10b981' : discountError ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '0.95rem',
            }}
          />
          <button
            type="button"
            onClick={handleApplyDiscount}
            disabled={discountLoading || !discountCode.trim()}
            style={{
              padding: '0.6rem 1.1rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: discountApplied ? '#10b981' : 'rgba(139,92,246,0.7)', color: 'white',
              fontWeight: '600', fontSize: '0.9rem', opacity: discountLoading ? 0.6 : 1,
            }}
          >
            {discountLoading ? '…' : discountApplied ? '✓' : 'Apply'}
          </button>
        </div>
        {discountApplied && (
          <p style={{ color: '#10b981', fontSize: '13px', margin: '-4px 0 10px 0' }}>🎉 {discountApplied.message}</p>
        )}
        {discountError && (
          <p style={{ color: '#f87171', fontSize: '13px', margin: '-4px 0 10px 0' }}>✗ {discountError}</p>
        )}

        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid rgba(255, 255, 255, 0.2)' }}>
          {!isDonationOnly && (
            <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>All prices include GST (10%)</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#d1d5db' }}>Subtotal:</span>
            <span style={{ color: 'white' }}>${subtotal.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#10b981' }}>Discount ({discountCode.toUpperCase()}):</span>
              <span style={{ color: '#10b981' }}>−${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#d1d5db' }}>
              Shipping: {shippingCost === 0 && subtotal > 100 && (
                <span style={{ color: '#10b981' }}>(Free over $100 subtotal)</span>
              )}
              {shippingCostLoading && shippingCost !== 0 && <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}> (calculating...)</span>}
            </span>
            <span style={{ color: 'white' }}>
              {shippingCostLoading && shippingCost !== 0 ? '...' : shippingCost === 0 ? <span style={{ color: '#10b981' }}>FREE</span> : `$${shippingCost.toFixed(2)}`}
            </span>
          </div>
          {!isDonationOnly && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#d1d5db' }}>GST included (est.):</span>
              <span style={{ color: 'white' }}>${tax.toFixed(2)}</span>
            </div>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '2px solid rgba(255, 255, 255, 0.3)',
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            <span style={{ color: 'white' }}>Total:</span>
            <span style={{
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}>
              ${total.toFixed(2)} AUD
            </span>
          </div>
        </div>
      </div>
      
      {/* Payment Information */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem',
          background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          fontWeight: 'bold'
        }}>
          💳 Payment Information
        </h2>
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              borderRadius: '12px',
              border: paymentMethod === 'card' ? '2px solid rgba(139, 92, 246, 0.8)' : '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.12)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={() => setPaymentMethod('card')}
              style={{ width: '20px', height: '20px', accentColor: '#8b5cf6' }}
            />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                Stripe Card • Zee now, pay now
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Pay in full instantly through Zee&apos;s secure Stripe gateway.
              </div>
            </div>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              borderRadius: '12px',
              border: paymentMethod === 'afterpay' ? '2px solid rgba(59, 130, 246, 0.8)' : '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(37, 99, 235, 0.25)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="afterpay"
              checked={paymentMethod === 'afterpay'}
              onChange={() => setPaymentMethod('afterpay')}
              style={{ width: '20px', height: '20px', accentColor: '#3b82f6' }}
            />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                Afterpay • Zee Flex Pay in 4
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                Split your Zee order into four fortnightly payments with no extra cost when you pay on time.
              </div>
            </div>
          </label>
        </div>

        {paymentMethod === 'card' ? (
          <>
            <div style={{
              padding: '2rem',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              minHeight: '200px'
            }}>
              {BYPASS_PAYMENT ? (
                <p style={{ margin: 0, color: '#111827', fontSize: '1rem', lineHeight: 1.5 }}>
                  Test bypass is on: card fields are hidden. Use <strong>Place Order</strong> below to finish the flow without Stripe (local/staging only).
                </p>
              ) : (
                <PaymentElement
                  options={{
                    // Stripe Link is a separate "save card" identity product — redundant when the
                    // customer is already signed in to Rainbow Sails (we already have their details above).
                    wallets: { link: isShopCustomer ? 'never' : 'auto' },
                  }}
                />
              )}
            </div>
            <p style={{
              marginTop: '1rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              🔒 256-bit SSL · Secure payment powered by Stripe
            </p>
          </>
        ) : (
          <div style={{
            padding: '1.5rem',
            background: 'rgba(59, 130, 246, 0.18)',
            borderRadius: '16px',
            border: '2px solid rgba(59, 130, 246, 0.35)',
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.25)'
          }}>
            <h3 style={{ marginBottom: '0.75rem', fontSize: '1.25rem', fontWeight: '700' }}>
              Zee&apos;s Afterpay Flex
            </h3>
            <p style={{ marginBottom: '0.75rem', fontSize: '1rem', color: 'rgba(255, 255, 255, 0.85)' }}>
              We&apos;ll whisk you to Afterpay to confirm the plan, then bring you back to finish your rainbow Zee receipt.
            </p>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: 0 }}>
              Eligibility is decided by Afterpay during their sign-in. No interest or fees when paid on schedule.
            </p>
          </div>
        )}
      </div>
      
      {/* Place Order Button */}
      <button
        type="submit"
        disabled={processing || (paymentMethod === 'card' && !BYPASS_PAYMENT && !stripe)}
        style={{
          width: '100%',
          padding: '1rem 2rem',
          fontSize: '1.25rem',
          fontWeight: 'bold',
          background: processing ? 'rgba(156, 163, 175, 0.5)' : 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          cursor: processing ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s',
          opacity: processing ? 0.6 : 1
        }}
      >
        {processing
          ? 'Processing...'
          : paymentMethod === 'afterpay'
            ? 'Continue to Afterpay with Zee'
            : `Place Order - $${total.toFixed(2)} AUD`}
      </button>
      
      <p style={{
        marginTop: '1rem',
        textAlign: 'center',
        fontSize: '0.875rem',
        color: '#9ca3af'
      }}>
        {paymentMethod === 'afterpay'
          ? '✨ You’ll complete payment on Afterpay and Zee will hold your cart until you return.'
          : '🔒 256-bit SSL · Secure payment powered by Stripe'}
      </p>
    </form>
    </div>
  );
};

const CustomCheckout = () => {
  console.log('🔍 CustomCheckout: Component rendered');
  console.log('🔍 CustomCheckout: React version:', React.version);
  console.log('🔍 CustomCheckout: Window object:', typeof window);
  console.log('🔍 CustomCheckout: Document object:', typeof document);
  
  const { cartItems, cartLoaded, getTotalPrice } = useCart();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [orderCompleted, setOrderCompleted] = useState(false);
  /** Cart total in cents the current PaymentIntent was created for — recreate PI when this changes (e.g. user removes event from cart) */
  const lastPaymentIntentAmountCentsRef = useRef(null);
  /** CheckoutForm’s payable total (cents) = same as “Place order” line — when set, must be used for PI (includes discount; parent calc did not) */
  const [formPayableCents, setFormPayableCents] = useState(null);
  const handleFormPayableCents = useCallback((c) => {
    setFormPayableCents(c);
  }, []);
  /** In-flight create-payment-intent: only the latest request may call setClientSecret (older responses would restore a higher $ amount) */
  const paymentIntentRequestGenRef = useRef(0);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeKeyLoaded, setStripeKeyLoaded] = useState(false);

  // Fetch Stripe publishable key from Business Integrations API on mount
  useEffect(() => {
    fetchStripePublishableKey().then((key) => {
      if (key) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 Stripe key loaded from integrations:', `${key.slice(0, 12)}…`);
        }
        setStripePromise(loadStripe(key));
      } else {
        console.error('❌ No Stripe publishable key found. Configure it in Business Integrations.');
        setStripePromise(Promise.resolve(null));
      }
      setStripeKeyLoaded(true);
    });
  }, []);
  
  console.log('🔍 CustomCheckout: Initial state - clientSecret:', clientSecret, 'loading:', loading);
  
  useEffect(() => {
    console.log('🔍 CustomCheckout useEffect - Debug Info:', {
      cartLoaded,
      cartItems,
      cartItemsLength: cartItems?.length,
      cartItemsType: typeof cartItems,
      getTotalPrice: getTotalPrice ? getTotalPrice() : 'no function'
    });
    
    console.log('🔍 CustomCheckout useEffect - Stripe Promise:', stripePromise);
    console.log('🔍 CustomCheckout useEffect - Stripe key loaded:', stripeKeyLoaded);
    
    logger.logComponentMount('CustomCheckout', {
      itemCount: cartItems?.length || 0,
      totalPrice: getTotalPrice ? getTotalPrice() : 0,
      cartLoaded: cartLoaded
    });
    
    // Only check if cart is empty after cart has loaded from localStorage
    // Don't redirect if we just completed an order
    if (cartLoaded && cartItems && cartItems.length === 0 && !orderCompleted) {
      console.log('❌ Cart is empty, redirecting to home');
      navigate('/');
      return;
    }
    
    if (cartLoaded && cartItems && cartItems.length > 0) {
      console.log('✅ Cart has items, proceeding with checkout');
    }
    
    // Create payment intent for Stripe
    const createPaymentIntent = async () => {
      if (!stripeKeyLoaded) return; // wait for key to load
      if (!stripePromise) {
        setLoading(false);
        return;
      }
      let myRequestGen = 0;
      try {
        const subtotal = getTotalPrice();
        // Match CheckoutForm: GST is in the subtotal; only add shipping. Physical carts use flat rate + free over $100.
        const PHYSICAL_TYPES = ['physical', 'print_on_demand', 'bundle', 'rental'];
        const hasPhysical = (cartItems || []).some(
          item => PHYSICAL_TYPES.includes(item.type) || PHYSICAL_TYPES.includes(item.productType)
        );
        let calculatedShippingCost = 0;
        if (hasPhysical) {
          if (subtotal > 100) {
            calculatedShippingCost = 0;
          } else {
            const cachedShipping = getCachedShippingCost();
            if (cachedShipping !== null && cachedShipping !== undefined) {
              calculatedShippingCost = cachedShipping;
            } else {
              try {
                const isProduction = typeof window !== 'undefined' &&
                  (window.location.hostname.includes('rainbow-sails.shop') ||
                    (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
                const apiBase = process.env.REACT_APP_API_URL ||
                  (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
                const response = await fetch(`${apiBase}/orders/get-shipping-cost`);
                if (response.ok) {
                  const data = await response.json();
                  calculatedShippingCost = data.shippingCost || 0;
                  if (calculatedShippingCost > 0) {
                    setCachedShippingCost(calculatedShippingCost);
                  }
                }
              } catch (err) {
                console.error('Error fetching shipping cost:', err);
                calculatedShippingCost = 0;
              }
            }
          }
        }
        const parentFallbackTotal = subtotal + calculatedShippingCost;
        const parentFallbackCents = Math.max(0, Math.round(parentFallbackTotal * 100));
        // When CheckoutForm is mounted, it owns the true charge (discount + its shipping) — no discount in parent, so this prevented matching the button
        const amountInCents = formPayableCents != null ? formPayableCents : parentFallbackCents;

        if (
          lastPaymentIntentAmountCentsRef.current != null &&
          lastPaymentIntentAmountCentsRef.current === amountInCents
        ) {
          setLoading(false);
          return;
        }

        myRequestGen = ++paymentIntentRequestGenRef.current;
        
        logger.log('info', 'checkout', 'Creating payment intent', {
          amount: amountInCents / 100,
          amountInCents: amountInCents,
          fromForm: formPayableCents != null
        });
        
        // Use the same API config as the rest of the frontend
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
        console.log('🔍 Calling backend:', backendUrl + '/api/v1/orders/create-payment-intent');
        const shopBusinessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();
        console.log('🔍 Request payload:', { amount: amountInCents, currency: 'aud', businessId: shopBusinessId || undefined });
        
        const response = await fetch(`${backendUrl}/api/v1/orders/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount: amountInCents,
            currency: 'aud',
            ...(shopBusinessId ? { businessId: shopBusinessId } : {}),
          })
        });
        
        console.log('🔍 Backend response status:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Backend error response body:', errorText);
          throw new Error(`Backend error: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        const data = await response.json();
        console.log('🔍 Backend response data:', JSON.stringify(data, null, 2));
        
        if (!data.success) {
          console.error('❌ Backend returned success: false, message:', data.message);
          throw new Error(data.message || 'Failed to create payment intent');
        }
        
        if (myRequestGen !== paymentIntentRequestGenRef.current) {
          console.log('⏭️ Ignoring stale payment intent response (newer cart/amount request in flight)');
          return;
        }

        console.log('🔍 CustomCheckout: Payment intent response:', data);
        console.log('🔍 CustomCheckout: Setting clientSecret:', data.clientSecret);
        
        lastPaymentIntentAmountCentsRef.current = amountInCents;
        setClientSecret(data.clientSecret);
        setLoading(false);
        
        console.log('🔍 CustomCheckout: State updated - clientSecret set, loading false');
        
        logger.log('info', 'checkout', 'Payment intent created', {
          clientSecret: data.clientSecret ? 'received' : 'missing'
        });
        
      } catch (error) {
        if (myRequestGen > 0 && myRequestGen !== paymentIntentRequestGenRef.current) {
          return;
        }
        console.error('❌ Failed to create payment intent:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        setLoading(false);
        
        logger.log('error', 'checkout', 'Payment intent failed', {
          error: error.message,
          stack: error.stack
        });
      }
    };
    
    if (cartLoaded && cartItems && cartItems.length > 0) {
      createPaymentIntent();
    }
  }, [cartItems, cartLoaded, getTotalPrice, formPayableCents, navigate, orderCompleted, stripeKeyLoaded, stripePromise]);

  if (stripeKeyLoaded && !stripePromise) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '520px',
          padding: '2rem',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>Stripe not configured</h2>
          <p style={{ color: '#e5e7eb', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Add your Stripe publishable key in the Dashboard under <strong>Business Integrations → Stripe</strong>.
            The shop will pick it up automatically — no rebuild needed.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#111827',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    console.log('🔍 CustomCheckout: Rendering loading state');
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            animation: 'spin 2s linear infinite'
          }}>
            🔄
          </div>
          <div style={{ fontSize: '1.5rem', color: 'white' }}>
            Preparing checkout...
          </div>
        </div>
      </div>
    );
  }
  
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#8b5cf6',
        colorBackground: '#ffffff',
        colorText: '#111827',
        colorDanger: '#ef4444',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
        spacingUnit: '4px',
        borderRadius: '8px'
      }
    }
  };
  
  // Only render Elements when clientSecret is available
  if (!clientSecret) {
    console.log('🔍 CustomCheckout: Rendering error state - no clientSecret');
    console.log('🔍 CustomCheckout: clientSecret value:', clientSecret);
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '2rem',
          background: 'rgba(239, 68, 68, 0.2)',
          borderRadius: '16px',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>
            ⚠️ Checkout Error
          </h2>
          <p style={{ color: '#fecaca', marginBottom: '1.5rem' }}>
            Failed to initialize payment. Please try again.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#111827',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }

  console.log('🔍 CustomCheckout: Rendering main checkout form');
  console.log('🔍 CustomCheckout: About to render Elements with:', {
    stripePromise: stripePromise,
    options: options,
    clientSecret: clientSecret
  });
  
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
      padding: '3rem 1rem'
    }}>
      <Elements key={clientSecret} stripe={stripePromise} options={options}>
        <CheckoutForm 
          clientSecret={clientSecret}
          processing={processing}
          setProcessing={setProcessing}
          error={error}
          setError={setError}
          orderCompleted={orderCompleted}
          setOrderCompleted={setOrderCompleted}
          onPayableAmountCentsChange={handleFormPayableCents}
        />
      </Elements>
    </div>
  );
};

export default CustomCheckout;

